import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { Users, RotateCcw, Trophy, X, Play, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { AuthContext, WS_URL } from "../App";

const COLORS = [
  { name: 'green', bg: 'bg-green-500', active: 'bg-green-300', sound: 261.63 },
  { name: 'red', bg: 'bg-red-500', active: 'bg-red-300', sound: 329.63 },
  { name: 'yellow', bg: 'bg-yellow-500', active: 'bg-yellow-300', sound: 392.00 },
  { name: 'blue', bg: 'bg-blue-500', active: 'bg-blue-300', sound: 523.25 }
];

const SimonSaysGame = ({ onClose }) => {
  const { token, user } = useContext(AuthContext);
  const [view, setView] = useState('lobby');
  const [currentTable, setCurrentTable] = useState(null);
  const [players, setPlayers] = useState([]);
  const [sequence, setSequence] = useState([]);
  const [playerSequence, setPlayerSequence] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isShowingSequence, setIsShowingSequence] = useState(false);
  const [activeColor, setActiveColor] = useState(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [scores, setScores] = useState({});
  const audioContext = useRef(null);
  const wsRef = useRef(null);

  // Initialize audio context
  useEffect(() => {
    audioContext.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContext.current) {
        audioContext.current.close();
      }
    };
  }, []);

  // Play sound for color
  const playSound = (frequency, duration = 300) => {
    if (!audioContext.current) return;
    
    const oscillator = audioContext.current.createOscillator();
    const gainNode = audioContext.current.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.current.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.current.currentTime + duration / 1000);
    
    oscillator.start(audioContext.current.currentTime);
    oscillator.stop(audioContext.current.currentTime + duration / 1000);
  };

  // Flash a color
  const flashColor = useCallback((colorIndex, duration = 500) => {
    return new Promise(resolve => {
      setActiveColor(colorIndex);
      playSound(COLORS[colorIndex].sound, duration);
      setTimeout(() => {
        setActiveColor(null);
        setTimeout(resolve, 100);
      }, duration);
    });
  }, []);

  // Show sequence to player
  const showSequence = useCallback(async () => {
    setIsShowingSequence(true);
    setPlayerSequence([]);
    
    await new Promise(r => setTimeout(r, 500));
    
    for (let i = 0; i < sequence.length; i++) {
      await flashColor(sequence[i]);
    }
    
    setIsShowingSequence(false);
  }, [sequence, flashColor]);

  // Add new color to sequence and show
  const nextRound = useCallback(() => {
    const newColor = Math.floor(Math.random() * 4);
    setSequence(prev => [...prev, newColor]);
  }, []);

  // Start game
  const startGame = () => {
    setSequence([]);
    setPlayerSequence([]);
    setScore(0);
    setGameOver(false);
    setIsPlaying(true);
    nextRound();
  };

  // Handle player input
  const handleColorClick = async (colorIndex) => {
    if (isShowingSequence || !isPlaying || gameOver) return;
    
    await flashColor(colorIndex, 200);
    
    const newPlayerSequence = [...playerSequence, colorIndex];
    setPlayerSequence(newPlayerSequence);
    
    // Check if correct
    const currentIndex = newPlayerSequence.length - 1;
    if (newPlayerSequence[currentIndex] !== sequence[currentIndex]) {
      // Wrong!
      setGameOver(true);
      setIsPlaying(false);
      if (score > highScore) {
        setHighScore(score);
      }
      toast.error(`Game Over! Score: ${score}`);
      return;
    }
    
    // Check if sequence complete
    if (newPlayerSequence.length === sequence.length) {
      setScore(prev => prev + 1);
      setTimeout(() => {
        nextRound();
      }, 1000);
    }
  };

  // Show sequence when it changes
  useEffect(() => {
    if (sequence.length > 0 && isPlaying) {
      showSequence();
    }
  }, [sequence, isPlaying, showSequence]);

  // WebSocket for multiplayer
  const connectWs = useCallback((tableCode) => {
    if (wsRef.current) wsRef.current.close();
    
    const ws = new WebSocket(`${WS_URL}/ws/simon/${tableCode}?token=${token}`);
    
    ws.onopen = () => {
      console.log("Simon WS connected");
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'game_state':
          setPlayers(data.players || []);
          setScores(data.scores || {});
          setCurrentPlayer(data.current_player);
          break;
        case 'player_score':
          setScores(prev => ({ ...prev, [data.user_id]: data.score }));
          break;
      }
    };
    
    wsRef.current = ws;
  }, [token]);

  // Create/Join game
  const createGame = () => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCurrentTable({ code });
    connectWs(code);
    setView('game');
  };

  const joinGame = (code) => {
    setCurrentTable({ code });
    connectWs(code);
    setView('game');
  };

  const leaveGame = () => {
    if (wsRef.current) wsRef.current.close();
    setView('lobby');
    setCurrentTable(null);
    setIsPlaying(false);
    setSequence([]);
    setScore(0);
    setGameOver(false);
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  if (view === 'lobby') {
    return (
      <div className="w-full h-full bg-gradient-to-b from-purple-900 to-indigo-950 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              🎯 SIMON SAYS
            </h2>
            <button onClick={onClose} className="text-white hover:text-red-400">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="bg-black/20 rounded-lg p-6 mb-4 text-center">
            <p className="text-gray-300 mb-4">Test your memory! Repeat the color sequence.</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={createGame}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 rounded-lg font-bold hover:opacity-90"
              >
                🎮 CREATE GAME
              </button>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  placeholder="Enter code..."
                  className="px-4 py-2 rounded bg-black/30 text-white border border-purple-600/50 uppercase text-center"
                  id="simon-code-input"
                />
                <button
                  onClick={() => {
                    const code = document.getElementById('simon-code-input').value;
                    if (code) joinGame(code.toUpperCase());
                  }}
                  className="bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-500"
                >
                  JOIN
                </button>
              </div>
            </div>
          </div>

          <div className="bg-black/20 rounded-lg p-4 text-gray-300 text-sm">
            <h3 className="font-bold text-purple-400 mb-2">HOW TO PLAY</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>Watch the color sequence carefully</li>
              <li>Repeat it by clicking the colors in order</li>
              <li>Each round adds one more color</li>
              <li>One mistake and it's game over!</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-b from-purple-900 to-indigo-950 flex flex-col">
      {/* Header */}
      <div className="bg-black/40 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-white font-bold">🎯 Game: {currentTable?.code}</span>
          <span className="text-yellow-400">Score: {score}</span>
          <span className="text-gray-400">High: {highScore}</span>
        </div>
        <button onClick={leaveGame} className="text-red-400 hover:text-red-300 text-sm">
          Leave
        </button>
      </div>

      {/* Game Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Simon Board */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {COLORS.map((color, index) => (
            <button
              key={color.name}
              onClick={() => handleColorClick(index)}
              disabled={isShowingSequence || !isPlaying}
              className={`w-32 h-32 rounded-lg transition-all duration-100 ${
                activeColor === index ? color.active : color.bg
              } ${isShowingSequence || !isPlaying ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110 active:brightness-125'}
              shadow-lg`}
            />
          ))}
        </div>

        {/* Status */}
        <div className="text-center">
          {!isPlaying && !gameOver && (
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-3 rounded-lg font-bold text-lg hover:from-green-400 hover:to-emerald-400 flex items-center gap-2"
            >
              <Play className="w-5 h-5" /> START GAME
            </button>
          )}
          
          {isShowingSequence && (
            <div className="text-yellow-400 text-xl font-bold animate-pulse">
              Watch carefully...
            </div>
          )}
          
          {isPlaying && !isShowingSequence && (
            <div className="text-white text-lg">
              Your turn! ({playerSequence.length}/{sequence.length})
            </div>
          )}
          
          {gameOver && (
            <div className="text-center">
              <div className="text-red-400 text-2xl font-bold mb-4">GAME OVER!</div>
              <div className="text-white text-lg mb-4">Final Score: {score}</div>
              <button
                onClick={startGame}
                className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-6 py-2 rounded-lg font-bold hover:opacity-90 flex items-center gap-2 mx-auto"
              >
                <RotateCcw className="w-4 h-4" /> PLAY AGAIN
              </button>
            </div>
          )}
        </div>

        {/* Multiplayer Scores */}
        {players.length > 1 && (
          <div className="mt-6 bg-black/30 rounded-lg p-3">
            <div className="text-purple-400 font-bold text-sm mb-2">LEADERBOARD</div>
            {Object.entries(scores)
              .sort(([,a], [,b]) => b - a)
              .map(([userId, playerScore], i) => (
                <div key={userId} className="flex items-center justify-between text-white text-sm">
                  <span>#{i + 1} {players.find(p => p.user_id === userId)?.username || 'Player'}</span>
                  <span className="font-bold">{playerScore}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimonSaysGame;
